import React, { useState, useEffect, useRef } from "react";
import DiffViewSplit from "../DiffViewSplit";
import DiffViewUnified from "../DiffViewUnified";
import SQLView from "components/SQLView";
import SQLEditor from "components/SQLEditor";
import { useDiffMode, useSetDiffMode } from "../DiffProvider";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronUp, faChevronDown } from "@fortawesome/pro-solid-svg-icons";
import classNames from "classnames";
import { DiffModeType } from "types/WorkbookDiff";

const DiffView = ({
  original,
  modified,
  isBaseline,
  defaultMode,
  canExpand,
  onChangeSQLEditor,
  className,
  childViewClassName = "",
}: {
  original: string;
  modified: string;
  isBaseline: boolean;
  defaultMode: DiffModeType;
  canExpand: boolean;
  onChangeSQLEditor?: (sql: string) => void;
  className: string;
  childViewClassName?: string;
}) => {
  const mode = useDiffMode();
  const setMode = useSetDiffMode();
  const wasLoaded = useLoadedModeCheck();
  const [isExpanded, setIsExpanded] = useState(!canExpand);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  // Reset to default mode when viewing baseline
  useEffect(() => {
    setMode(defaultMode);
  }, [defaultMode, isBaseline, setMode]);

  function useLoadedModeCheck(): (mode: DiffModeType) => boolean {
    const loaded = useRef(new Set<DiffModeType>());
    const currMode = useDiffMode();
    loaded.current.add(currMode);
    return (mode: DiffModeType) => loaded.current.has(mode);
  }

  return (
    <div>
      <div
        className={classNames(
          isExpanded || !canExpand ? "" : "h-[280px]",
          className,
        )}
      >
        {wasLoaded("diff-split") && (
          <DiffViewSplit
            original={original}
            modified={modified}
            className={classNames(
              childViewClassName,
              mode !== "diff-split" && "hidden",
            )}
          />
        )}
        {wasLoaded("diff-unified") && (
          <DiffViewUnified
            original={original}
            modified={modified}
            className={classNames(
              childViewClassName,
              mode !== "diff-unified" && "hidden",
            )}
          />
        )}
        {wasLoaded("read") && (
          <SQLView
            sql={modified}
            className={classNames(
              childViewClassName,
              mode !== "read" && "hidden",
            )}
          />
        )}
        {wasLoaded("write") && (
          <SQLEditor
            sql={modified}
            onChange={onChangeSQLEditor}
            className={classNames(
              childViewClassName,
              mode !== "write" && "hidden",
            )}
          />
        )}
      </div>
      {canExpand && (
        <div className="mt-4 flex justify-center">
          <button
            className="bg-white text-black shadow-sm inline-flex items-center justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors"
            onClick={toggleExpanded}
          >
            <FontAwesomeIcon
              icon={isExpanded ? faChevronUp : faChevronDown}
              className="mr-2"
            />
            {isExpanded ? "Show less code" : "Show all code"}
          </button>
        </div>
      )}
    </div>
  );
};

export default DiffView;
